import { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _isUndefined from 'lodash/isUndefined';
import _find from 'lodash/find';
import _reduce from 'lodash/reduce';
import { gqlProfileSearchInput, gqlSearchResultType } from '@minecraft.graphql-types';
import { useDrawerToggle, useDrawerFiltersCounts } from '@minecraft.drawer';
import { FiltersCountNameEnum } from '../types';
import {
  QUERY_PROFILES_FILTER_DRAWER_COUNTS,
  QUERY_SUBMISSIONS_FILTER_DRAWER_COUNTS,
  QUERY_TALENTSCOUT_FILTER_DRAWER_COUNTS,
} from '../graphqls';

type UseFilterDrawerCountsArgs = {
  isSubmissionContext: boolean;
  roleId: number;
  submissionStatusCode: string;
  appliedQueryInput: gqlProfileSearchInput;
  isTalentScout?: boolean;
};

const useFilterDrawerCounts = ({
  isSubmissionContext,
  roleId,
  submissionStatusCode,
  appliedQueryInput,
  isTalentScout,
}: UseFilterDrawerCountsArgs): {
  refetchProfileFilterData?: () => void;
} => {
  const { isDrawerOpened } = useDrawerToggle();
  const { handleFiltersCountsChange } = useDrawerFiltersCounts();

  const { data: dataProfiles, refetch: refetchProfileFilterData } = useQuery(QUERY_PROFILES_FILTER_DRAWER_COUNTS, {
    variables: {
      input: { ...appliedQueryInput, skills: appliedQueryInput?.skills || [] },
      resultType: gqlSearchResultType.FACETS,
    },
    fetchPolicy: 'cache-and-network',
    skip: isTalentScout || isSubmissionContext || !isDrawerOpened || _isUndefined(appliedQueryInput),
  });

  const { data: dataSubmissions } = useQuery(QUERY_SUBMISSIONS_FILTER_DRAWER_COUNTS, {
    variables: {
      input: appliedQueryInput,
      roleId,
      submissionStatusCode,
      resultType: gqlSearchResultType.FACETS,
    },
    fetchPolicy: 'cache-and-network',
    skip: isTalentScout || !isSubmissionContext || !isDrawerOpened || _isUndefined(appliedQueryInput),
  });

  const { data: talentScoutCounts } = useQuery(QUERY_TALENTSCOUT_FILTER_DRAWER_COUNTS, {
    variables: {
      input: appliedQueryInput,
      roleId,
      submissionStatusCode,
      resultType: gqlSearchResultType.FACETS,
    },
    fetchPolicy: 'cache-and-network',
    skip: !isTalentScout || !isDrawerOpened || _isUndefined(appliedQueryInput),
  });

  useEffect(() => {
    const filterDrawerCounts =
      dataProfiles?.profilesFilterDrawerCounts?.facets ||
      dataSubmissions?.submissionsFilterDrawerCounts?.facets ||
      talentScoutCounts?.talentScoutFilterCounts?.facets;

    if (!_isUndefined(filterDrawerCounts)) {
      const filtersCounts = _reduce(
        Object.values(FiltersCountNameEnum),
        (acc, key) => {
          return {
            ...acc,
            [key]: _find(filterDrawerCounts, { name: key })?.buckets || [],
          };
        },
        {}
      );

      handleFiltersCountsChange(filtersCounts);
    }
  }, [dataProfiles, dataSubmissions, talentScoutCounts]);

  return {
    refetchProfileFilterData,
  };
};

export default useFilterDrawerCounts;
